import React, { useState, useEffect } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Send, Edit2, Filter, CheckCircle2, XCircle, Clock, Network } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodelcoVehiculosPage() {
    const [vehiculos, setVehiculos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [filter, setFilter] = useState('all'); // all | integrados | no_integrados | con_errores
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [sendingId, setSendingId] = useState('');
    const navigate = useNavigate();

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/vehiculos`, { headers });
            if (r.ok) setVehiculos(await r.json());
        } catch { /* ignore */ }
        setLoading(false);
    };

    const syncFromFoursat = async () => {
        setSyncing(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/sync-devices`, { method: 'POST', headers });
            const d = await r.json();
            if (r.ok) {
                toast.success(`Sincronizado: ${d.nuevos} nuevos, ${d.actualizados} actualizados`);
                load();
            } else {
                toast.error(d.detail || 'Error al sincronizar con 4sat');
            }
        } catch { toast.error('Error de conexión'); }
        setSyncing(false);
    };

    const toggleIntegrado = async (v) => {
        const newVal = !v.integrado;
        // Optimistic update
        setVehiculos(prev => prev.map(x => x.id === v.id ? { ...x, integrado: newVal } : x));
        try {
            await fetch(`${API_URL}/api/codelco/vehiculos/${v.id}`, {
                method: 'PUT', headers,
                body: JSON.stringify({ integrado: newVal }),
            });
        } catch {
            toast.error('Error al actualizar');
            setVehiculos(prev => prev.map(x => x.id === v.id ? { ...x, integrado: !newVal } : x));
        }
    };

    const enviarAhora = async (v) => {
        setSendingId(v.id);
        try {
            const r = await fetch(`${API_URL}/api/codelco/vehiculos/${v.id}/enviar-ahora`, { method: 'POST', headers });
            const d = await r.json();
            if (d.ok) toast.success(`Enviado: ${v.patente}`);
            else toast.error(`Error: ${d.mensaje?.substring(0, 100)}`);
            load();
        } catch { toast.error('Error'); }
        setSendingId('');
    };

    const sendAll = async () => {
        setSyncing(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/sincronizar-todos`, { method: 'POST', headers });
            const d = await r.json();
            toast.success(`Enviados ${d.enviados} OK, ${d.errores} errores de ${d.total}`);
            load();
        } catch { toast.error('Error'); }
        setSyncing(false);
    };

    const openEdit = (v) => {
        setEditing(v);
        setEditForm({
            pgps: v.pgps || '4sat',
            tercero: v.tercero || 'Codelco DCH',
            empresa: v.empresa || 'codelcodch',
            conductor: v.conductor || 'No Asignado',
            numSAT: v.numSAT || v.imei || '',
        });
    };

    const saveEdit = async () => {
        try {
            const r = await fetch(`${API_URL}/api/codelco/vehiculos/${editing.id}`, {
                method: 'PUT', headers,
                body: JSON.stringify(editForm),
            });
            if (r.ok) {
                toast.success('Actualizado');
                setEditing(null);
                load();
            } else {
                const e = await r.json();
                toast.error(e.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    const filtered = vehiculos.filter(v => {
        if (filter === 'integrados' && !v.integrado) return false;
        if (filter === 'no_integrados' && v.integrado) return false;
        if (filter === 'con_errores' && v.ultimo_envio_ok !== false) return false;
        if (search && !`${v.patente} ${v.imei}`.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const counts = {
        total: vehiculos.length,
        integrados: vehiculos.filter(v => v.integrado).length,
        errores: vehiculos.filter(v => v.ultimo_envio_ok === false).length,
    };

    return (
        <CodelcoLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight">Vehículos</h1>
                        <p className="text-muted-foreground">{counts.total} total · {counts.integrados} integrados · {counts.errores} con errores</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={syncFromFoursat} disabled={syncing} data-testid="btn-sync-4sat">
                            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                            Sincronizar desde 4sat
                        </Button>
                        <Button onClick={sendAll} disabled={syncing} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="btn-send-all">
                            <Send className="h-4 w-4 mr-2" /> Enviar todos
                        </Button>
                    </div>
                </div>

                <Card className="border-2">
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex gap-1 flex-wrap">
                                {[
                                    { v: 'all', l: 'Todos' },
                                    { v: 'integrados', l: 'Integrados' },
                                    { v: 'no_integrados', l: 'No integrados' },
                                    { v: 'con_errores', l: 'Con errores' },
                                ].map(f => (
                                    <Button key={f.v} variant={filter === f.v ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}>
                                        {f.l}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <Input placeholder="Buscar patente o IMEI..." value={search} onChange={e => setSearch(e.target.value)} data-testid="input-search" />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-8 text-center text-muted-foreground">Cargando...</div>
                        ) : filtered.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                {vehiculos.length === 0
                                    ? 'No hay vehículos sincronizados. Toca "Sincronizar desde 4sat" para cargarlos.'
                                    : 'Sin resultados para el filtro actual'}
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-12">Env.</TableHead>
                                        <TableHead>Patente</TableHead>
                                        <TableHead>IMEI</TableHead>
                                        <TableHead>Conductor</TableHead>
                                        <TableHead>numSAT</TableHead>
                                        <TableHead>Último envío</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(v => (
                                        <TableRow key={v.id} data-testid={`row-vehiculo-${v.id}`}>
                                            <TableCell>
                                                <Checkbox checked={!!v.integrado} onCheckedChange={() => toggleIntegrado(v)} data-testid={`chk-${v.id}`} />
                                            </TableCell>
                                            <TableCell className="font-bold font-mono">{v.patente}</TableCell>
                                            <TableCell className="font-mono text-xs">{v.imei || '-'}</TableCell>
                                            <TableCell className="text-sm">{v.conductor || 'No Asignado'}</TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {v.ultimo_estado?.numSAT !== undefined ? (
                                                    <span title="Satélites en último envío">{v.ultimo_estado.numSAT}</span>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {!v.ultimo_envio_at ? (
                                                    <Badge variant="outline" className="text-muted-foreground">
                                                        <Clock className="h-3 w-3 mr-1" /> Nunca
                                                    </Badge>
                                                ) : v.ultimo_envio_ok ? (
                                                    <div className="text-xs">
                                                        <Badge className="bg-emerald-500 text-white">
                                                            <CheckCircle2 className="h-3 w-3 mr-1" /> OK
                                                        </Badge>
                                                        <div className="text-muted-foreground mt-1">{new Date(v.ultimo_envio_at).toLocaleString()}</div>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs">
                                                        <Badge variant="destructive">
                                                            <XCircle className="h-3 w-3 mr-1" /> Error
                                                        </Badge>
                                                        <div className="text-muted-foreground mt-1 truncate max-w-[200px]" title={v.ultimo_envio_msg}>
                                                            {(v.ultimo_envio_msg || '').substring(0, 50)}
                                                        </div>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="icon" onClick={() => navigate(`/codelco/mapeo?vid=${v.id}`)} title="Configurar mapeo XML" data-testid={`btn-mapeo-${v.id}`}>
                                                        <Network className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => openEdit(v)} title="Editar metadata" data-testid={`btn-edit-${v.id}`}>
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => enviarAhora(v)} disabled={sendingId === v.id} title="Enviar ahora">
                                                        <Send className={`h-4 w-4 ${sendingId === v.id ? 'animate-pulse' : ''}`} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar metadata Codelco — {editing?.patente}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Proveedor GPS (&lt;pgps&gt;)</Label>
                            <Input value={editForm.pgps || ''} onChange={e => setEditForm({ ...editForm, pgps: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Tercero (cliente)</Label>
                            <Input value={editForm.tercero || ''} onChange={e => setEditForm({ ...editForm, tercero: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Empresa (&lt;empresa&gt;)</Label>
                            <Input value={editForm.empresa || ''} onChange={e => setEditForm({ ...editForm, empresa: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Conductor</Label>
                            <Input value={editForm.conductor || ''} onChange={e => setEditForm({ ...editForm, conductor: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>numSAT</Label>
                            <Input value={editForm.numSAT || ''} onChange={e => setEditForm({ ...editForm, numSAT: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                        <Button onClick={saveEdit} data-testid="btn-save-edit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </CodelcoLayout>
    );
}
